import {
  ApolloClient,
  InMemoryCache,
  type OperationVariables,
  type DocumentNode,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import React from "react";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";

const httpLink = createUploadLink({
  uri: import.meta.env.VITE_GRAPHQL_URL || window.location.origin + "/graphql",
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from session storage if it exists
  const token = sessionStorage.getItem("token");

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `bearer ${token}` : "",
      "Content-Type": "application/json",
      "apollo-require-preflight": true,
    },
  };
});

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (
        err.extensions?.code === "UNAUTHENTICATED" ||
        err.message.includes("Your account is disabled")
      ) {
        const isDisabled = err.message.includes("Your account is disabled");
        const msg = isDisabled
          ? "Your account has been disabled by an administrator. You have been logged out."
          : "Your session has expired. Please log in again.";

        sessionStorage.clear();
        localStorage.clear();
        window.alert(msg);
        window.location.href = "/";
      }
    }
  }
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});
export default client;

/**
 * useLazyQuery
 *
 * Returns a function that will execute a lazy query
 * when called with variables. The result of the query
 * will be a Promise that resolves to the result of the
 * query.
 *
 * @param query the GraphQL query to execute
 * @returns a function that takes the variables as
 * an argument and returns a Promise that resolves to
 * the result of the query.
 */
export function useLazyQuery<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(query: DocumentNode) {
  return React.useCallback(
    (variables: TVariables) =>
      client.query<TData, TVariables>({
        query: query,
        variables: variables,
        fetchPolicy: "network-only",
      }),
    [client, query]
  );
}
